<template>
    <div class="kb-tree-item">
        <div class="kb-section-header" @click="handleClick" :data-level="level">
            <svg class="kb-chevron" :class="{ rotated: expanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
            <span class="kb-section-title" :class="{ active: isActive }">{{ item.name }}</span>
        </div>

        <!-- Children sections -->
        <div v-if="hasChildren" class="kb-children" :class="{ expanded: expanded }">
            <menu-tree-item
                v-for="child in item.children"
                :key="child.id"
                :item="child"
                :level="level + 1"
                :hash="hash"
                :item-route-name="itemRouteName"
                @loadItems="$emit('loadItems', $event)"
                @itemClick="$emit('itemClick', $event)"
            />
        </div>

        <!-- Items list -->
        <div v-if="itemsLoaded || loading" class="kb-items-list" :class="{ expanded: expanded }">
            <div v-if="loading" class="kb-loading">
                <svg class="kb-spinner" viewBox="0 0 24 24">
                    <circle class="kb-spinner-path" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>
                </svg>
                <span class="kb-loading-text">Загрузка...</span>
            </div>
            <div v-if="!loading && !hasItems" class="kb-empty">
                <span class="kb-empty-text">Нет элементов</span>
            </div>
            <div class="kb-item" v-for="item in items" :key="item.id" @click="handleItemClick(item)" :class="{ 'kb-item--active': item.id == activeItemId }" :data-level="level">
                <span class="kb-item-text">{{ item.name }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MenuTreeItem',
        emits: ['loadItems', 'itemClick'],
        props: {
            item: {
                type: Object,
                required: true
            },
            level: {
                type: Number,
                default: 0
            },
            hash: {
                type: String,
                required: true
            },
            itemRouteName: {
                type: String,
                default: 'section-detail-item'
            }
        },
        data() {
            return {
                expanded: false,
                items: [],
                itemsLoaded: false,
                loading: false,
                hasItems: false
            }
        },
        computed: {
            hasChildren() {
                return this.item.children && Array.isArray(this.item.children) && this.item.children.length > 0;
            },
            isActive() {
                const currentSectionId = this.$route.params.sectionId;
                return currentSectionId == this.item.id;
            },
            isCurrentItemInThisSection() {
                // Check if current itemId is in this section's items
                const currentItemId = this.$route.params.itemId;
                if (!currentItemId || !this.itemsLoaded) return false;
                return this.items.some(item => item.id == currentItemId);
            },
            activeItemId() {
                return this.$route.params.itemId;
            }
        },
        methods: {
            handleClick() {
                // Always toggle expansion
                this.expanded = !this.expanded;

                // Load items if not loaded yet
                if (!this.itemsLoaded && !this.loading) {
                    this.loadItems();
                }
            },
            loadItems() {
                if (this.loading || this.itemsLoaded) return;

                this.loading = true;
                this.$emit('loadItems', {
                    sectionId: this.item.id,
                    hash: this.hash,
                    component: this
                });
            },
            handleItemClick(item) {
                this.$emit('itemClick', item);
                this.$router.push({
                    name: this.itemRouteName,
                    params: { itemId: item.id },
                    query: { hash: this.hash }
                });
            },
            setItems(items) {
                this.items = items;
                this.itemsLoaded = true;
                this.loading = false;
                this.hasItems = items && items.length > 0;
                // Always expand when items are loaded
                this.expanded = true;
            },
            isDescendantSelected(sectionId) {
                if (!this.hasChildren) return false;

                for (const child of this.item.children) {
                    if (child.id == sectionId) {
                        return true;
                    }
                    if (child.children && this.hasDescendant(child, sectionId)) {
                        return true;
                    }
                }
                return false;
            },
            hasDescendant(node, sectionId) {
                if (!node.children) return false;

                for (const child of node.children) {
                    if (child.id == sectionId) {
                        return true;
                    }
                    if (child.children && this.hasDescendant(child, sectionId)) {
                        return true;
                    }
                }
                return false;
            }
        },
        watch: {
            '$route.params.itemId': {
                immediate: true,
                handler(newItemId) {
                    // If this section has children, check if descendant is selected
                    if (this.hasChildren && this.isDescendantSelected(this.$route.params.sectionId)) {
                        this.expanded = true;
                    }
                    // If current item is in this section, expand it
                    if (this.itemsLoaded && this.isCurrentItemInThisSection) {
                        this.expanded = true;
                    }
                    // Load items when route changes to an itemId
                    if (newItemId && !this.itemsLoaded && !this.loading) {
                        this.loadItems();
                    }
                }
            },
            '$route.params.sectionId': {
                immediate: true,
                handler(newSectionId) {
                    if (this.hasChildren && this.isDescendantSelected(newSectionId)) {
                        this.expanded = true;
                    }
                }
            }
        },
        created() {
            if (this.hasChildren && this.isDescendantSelected(this.$route.params.sectionId)) {
                this.expanded = true;
            }
        },
        mounted() {
            // If items are already loaded, expand if current item is here
            if (this.itemsLoaded && this.isCurrentItemInThisSection) {
                this.expanded = true;
            }
        }
    }
</script>

<style scoped>
.kb-tree-item {
    width: 100%;
}

.kb-section-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    user-select: none;
    text-decoration: none;
    color: inherit;
}

/* First level sections have more padding */
.kb-section-header[data-level="0"] {
    padding: 12px 24px !important;
    font-weight: 600;
}

.kb-section-header[data-level="1"] {
    padding: 10px 20px !important;
    padding-left: 36px !important;
    font-weight: 500;
}

.kb-section-header[data-level="2"] {
    padding: 8px 16px !important;
    padding-left: 52px !important;
    font-weight: 500;
}

.kb-section-header[data-level="3"] {
    padding: 8px 16px !important;
    padding-left: 68px !important;
    font-weight: 400;
}

.kb-section-header[data-level="4"] {
    padding: 8px 16px !important;
    padding-left: 84px !important;
    font-weight: 400;
}

.kb-section-header:hover {
    background-color: #f9fafb;
}

.kb-chevron {
    width: 14px;
    height: 14px;
    color: #9ca3af;
    flex-shrink: 0;
    margin-right: 6px;
    transition: transform 0.2s;
}

.kb-chevron.rotated {
    transform: rotate(180deg);
}

.kb-section-title {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    text-decoration: none;
    word-wrap: break-word;
    hyphens: auto;
    transition: color 0.2s;
}

.kb-section-header:hover .kb-section-title {
    color: #1f2937;
}

.kb-section-title.active {
    color: #3b82f6;
}

.kb-children {
    display: none;
    overflow: hidden;
}

.kb-children.expanded {
    display: block;
}

.kb-items-list {
    display: none;
    overflow: hidden;
}

.kb-items-list.expanded {
    display: block;
}

.kb-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px 8px 40px;
    font-size: 13px;
    color: #9ca3af;
}

.kb-spinner {
    width: 16px;
    height: 16px;
    animation: kb-spin 1s linear infinite;
}

.kb-spinner-path {
    stroke: #9ca3af;
    stroke-linecap: round;
    stroke-dasharray: 28, 36;
}

@keyframes kb-spin {
    to {
        transform: rotate(360deg);
    }
}

.kb-loading-text {
    font-size: 12px;
}

.kb-empty {
    padding: 8px 12px 8px 40px;
    font-size: 13px;
    color: #9ca3af;
    font-style: italic;
}

.kb-empty-text {
    font-size: 12px;
}

.kb-item {
    padding: 8px 12px 8px 40px;
    font-size: 13px;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
    word-wrap: break-word;
    line-height: 1.5;
    border-left: 2px solid transparent;
}

/* Item padding by level */
.kb-item[data-level="0"] {
    padding: 10px 12px 10px 44px !important;
}

.kb-item[data-level="1"] {
    padding: 8px 12px 8px 60px !important;
}

.kb-item[data-level="2"] {
    padding: 8px 12px 8px 76px !important;
}

.kb-item[data-level="3"] {
    padding: 8px 12px 8px 92px !important;
}

.kb-item[data-level="4"] {
    padding: 8px 12px 8px 108px !important;
}

.kb-item:hover {
    background-color: #f9fafb;
    color: #374151;
    border-left-color: #9ca3af;
}

.kb-item--active {
    background-color: #eff6ff;
    color: #1d4ed8;
    border-left-color: #3b82f6;
    font-weight: 500;
}

.kb-item-text {
    word-wrap: break-word;
    hyphens: auto;
}
</style>
